// ============================================================
// Device self-enrolment API (production contract)
// ------------------------------------------------------------
// Powers the public /enrol/device page on console.smplify.com.
// Mirrors the real flow used by portal.smplify.com — verified
// from a captured success HAR (docs/backend-requirements/
// device-enrolment-api.md).
//
// The flow is two API calls + one navigation:
//
//   1. POST /api/v1/device/authenticate
//        body : { username, password }
//        200  : ResponseDTO<{ token: string }>   // JWT
//        401  : invalid credentials
//
//   2. POST /api/v1/device/terms-of-service?accept=true
//        headers: Authorization: Bearer <jwt from step 1>
//        body   : (empty / "accept=true")
//        302    : Location header points to
//                 /api/v1/mdm/device/enroll.mobileconfig
//                   ?d=<device-uuid>&challenge=<token>
//
//   3. The browser navigates to that Location URL, which streams
//      back the signed .mobileconfig the device installs.
//
// Both endpoints are public from a tenant-routing perspective:
// the user authenticates inline in step 1 and the JWT carries
// tenantId for step 2. We must therefore NOT send the X-Tenant-Id
// header on either call.
// ============================================================

import { apiPost, unwrap } from "./apiClient";
import type { ResponseDTO } from "@/types/api";

/**
 * Thrown when the backend reports the device/user is awaiting
 * administrator approval (`status: "pending_approval"` on either
 * the authenticate or terms-of-service response).
 *
 * Callers should redirect to /portal/pending-approval rather than
 * surface a generic error.
 */
export class PendingApprovalError extends Error {
  readonly code = "PENDING_APPROVAL" as const;
  constructor(message = "Your device registration is awaiting administrator approval.") {
    super(message);
    this.name = "PendingApprovalError";
  }
}

function isPendingApprovalPayload(payload: unknown): boolean {
  if (!payload || typeof payload !== "object") return false;
  const root = payload as Record<string, unknown>;
  const candidates = [root.status, (root.data as any)?.status, (root.error as any)?.status];
  return candidates.some(
    (s) => typeof s === "string" && s.toLowerCase() === "pending_approval"
  );
}

// ---- Step 1: authenticate ----------------------------------

export interface DeviceAuthenticateRequest {
  username: string;
  password: string;
}

export interface DeviceAuthenticateResponse {
  /** Short-lived JWT used to call the TOS endpoint. */
  token: string;
}

export async function authenticateDevice(
  body: DeviceAuthenticateRequest
): Promise<DeviceAuthenticateResponse> {
  const res = await apiPost<ResponseDTO<DeviceAuthenticateResponse> & { status?: string }>(
    "/api/v1/device/authenticate",
    body,
    { includeTenantHeader: false } as any
  );
  if (isPendingApprovalPayload(res)) {
    throw new PendingApprovalError();
  }
  return unwrap(res);
}

// ---- Step 2: accept Terms of Service -----------------------
//
// The TOS endpoint replies with a 302 whose Location header is
// the .mobileconfig download URL. Browsers do not let `fetch`
// read 302 Location headers when `redirect: 'follow'` is set
// (the response body is the eventual target), and `redirect:
// 'manual'` produces an opaque response we also can't read.
//
// The pragmatic, browser-safe approach used by the production
// portal is to **navigate** the page to the TOS URL with the
// Bearer token NOT in a header but on a server-issued one-shot
// redirect. Because we can't set Authorization on a navigation,
// we instead fetch with redirect:'manual' and ask the backend to
// also return the resolved enrollment URL in the response body
// when the request includes `Accept: application/json`.
//
// Until the backend ships that JSON variant we expose two
// helpers and let the caller decide:
//
//  - acceptTermsAndGetEnrollmentUrl()  → reads JSON {enrollmentUrl}
//    (preferred; requires BE change documented in docs/)
//  - buildTermsOfServiceUrl(token)     → returns the URL the
//    page can open in a new tab so the browser follows the 302
//    natively. Works today.

export interface AcceptTermsResponse {
  /**
   * Fully-qualified URL the device must open to download the
   * signed .mobileconfig (e.g.
   * https://api.smplify.com/api/v1/mdm/device/enroll.mobileconfig
   * ?d=<uuid>&challenge=<token>).
   */
  enrollmentUrl: string;
}

const API_ORIGIN = "https://api.smplify.com";

/**
 * Build the URL the page can navigate to once the user has a
 * valid JWT. The backend reads the JWT from the `Authorization`
 * header — so this only works for fetch/XHR, not a top-level
 * navigation. Use {@link acceptTermsAndGetEnrollmentUrl} for
 * the navigation-friendly variant.
 */
export function buildTermsOfServiceUrl(): string {
  return `${API_ORIGIN}/api/v1/device/terms-of-service?accept=true`;
}

/**
 * Calls POST /device/terms-of-service?accept=true with the JWT
 * and resolves to the enrollment URL the device should open.
 *
 * Two response shapes are tolerated, in order of preference:
 *   1. `ResponseDTO<{ enrollmentUrl }>`  ← preferred, BE work
 *      requested in docs/backend-requirements/device-enrolment-api.md
 *   2. A 302 whose `Location` header is the enrollment URL.
 *      Most browsers transparently follow this; if `fetch`
 *      surfaces it as `response.url` we use that.
 */
export async function acceptTermsAndGetEnrollmentUrl(
  token: string
): Promise<AcceptTermsResponse> {
  const url = buildTermsOfServiceUrl();
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Partner": "portal",
    },
    body: JSON.stringify({}),
    // Let the browser follow the redirect — `response.url`
    // will then expose the final URL.
    redirect: "follow",
  });

  if (!res.ok && res.status !== 0) {
    let message = `Terms of service request failed (${res.status})`;
    try {
      const data = await res.json();
      message = data?.error?.message || data?.message || message;
    } catch {
      /* non-JSON body — keep default message */
    }
    throw new Error(message);
  }

  // Preferred path: BE returns JSON body with enrollmentUrl.
  const contentType = res.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    const data = (await res.json()) as ResponseDTO<AcceptTermsResponse> & {
      status?: string;
    };
    if (isPendingApprovalPayload(data)) {
      throw new PendingApprovalError();
    }
    if (data?.data?.enrollmentUrl) {
      return { enrollmentUrl: data.data.enrollmentUrl };
    }
  }

  // Fallback: the browser followed the 302 silently — use the
  // resolved URL.
  if (res.url && res.url.includes("enroll.mobileconfig")) {
    return { enrollmentUrl: res.url };
  }

  throw new Error(
    "Enrollment URL was not returned by the server. Please contact your administrator."
  );
}

// ---- Convenience wrapper used by the page ------------------

export interface EnrolDeviceRequest {
  email: string;
  password: string;
}

export interface EnrolDeviceResult {
  enrollmentUrl: string;
}

/**
 * Full happy-path: authenticate then accept the TOS, returning
 * the .mobileconfig URL the device should open.
 */
export async function enrolDevice(
  body: EnrolDeviceRequest
): Promise<EnrolDeviceResult> {
  const { token } = await authenticateDevice({
    username: body.email,
    password: body.password,
  });
  return acceptTermsAndGetEnrollmentUrl(token);
}
